import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, Check, CalendarIcon, Coffee, Heart, Cake, Briefcase, Clock, XCircle, Shirt, Star, ChevronRight, Store, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import API from "@/lib/api";

const ICON_MAP = {
  coffee: Coffee, heart: Heart, cake: Cake, briefcase: Briefcase,
  clock: Clock, "x-circle": XCircle, shirt: Shirt, star: Star, info: Star, calendar: CalendarIcon,
};

function ProgressBar({ current, total }) {
  return (
    <div className="flex items-center justify-center gap-1 py-3" data-testid="progress-bar">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className={`progress-dot ${i < current ? 'completed' : ''} ${i === current ? 'active' : ''}`} />
          {i < total - 1 && <div className={`progress-line ${i < current ? 'completed' : ''}`} />}
        </div>
      ))}
    </div>
  );
}

export default function ReservationWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';
  const presetSedeId = new URLSearchParams(window.location.search).get('sedeId');
  const [sedes, setSedes] = useState([]);
  const [reservationPurposes, setEventTypes] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [selectedSede, setSelectedSede] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "", email: "", phone: "", countryCode: "+57", numPeople: "",
  });
  const [eventData, setEventData] = useState({
    reservationPurpose: "", date: null, time: "", comments: "",
  });
  const [selectedZone, setSelectedZone] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [allZoneTypes, setAllTableTypes] = useState([]);
  const [landingConfig, setLandingConfig] = useState({ whatsapp_link: "", corporate_threshold: 0, booking_lead_hours: 0 });

  useEffect(() => {
    Promise.all([
      API.get("/sedes").then(r => {
        setSedes(r.data);
        if (presetSedeId) {
          const preset = r.data.find(s => s.id === presetSedeId);
          if (preset) {
            setSelectedSede(preset);
            setSelectedCity(preset.city || "Sin ciudad");
            setStep(1);
          }
        }
      }),
      API.get("/reservation-purposes").then(r => setEventTypes(r.data)),
      API.get("/zone-types").then(r => setAllTableTypes(r.data)),
      API.get("/landing").then(r => setLandingConfig({
        whatsapp_link: r.data.whatsapp_link || "",
        corporate_threshold: r.data.corporate_threshold || 0,
        booking_lead_hours: r.data.booking_lead_hours || 0,
      })).catch(() => {}),
    ]).catch(() => toast.error("Error cargando datos"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Embed mode: send height to parent.
  // SECURITY: target the parent's origin explicitly (derived from
  // document.referrer at iframe-load time) instead of '*'. This prevents the
  // browser from delivering our messages to any site that might intercept the
  // iframe via DOM tricks. We cache the origin to avoid recomputing each tick.
  const parentOrigin = (() => {
    try {
      if (document.referrer) return new URL(document.referrer).origin;
    } catch { /* ignore */ }
    return null;
  })();

  useEffect(() => {
    if (!isEmbed) return;
    const sendHeight = () => {
      const h = document.documentElement.scrollHeight;
      if (parentOrigin) {
        window.parent.postMessage({ type: 'reserva-widget-resize', height: h }, parentOrigin);
      }
    };
    sendHeight();
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => observer.disconnect();
  }, [isEmbed, step, parentOrigin]);

  useEffect(() => {
    if (selectedSede) {
      API.get(`/sedes/${selectedSede.id}/blocked-dates`)
        .then(r => setBlockedDates(r.data))
        .catch(() => {});
      API.get(`/conditions?sede_id=${selectedSede.id}`)
        .then(r => setConditions(r.data.filter(c => c.active !== false)))
        .catch(() => {});
    }
  }, [selectedSede]);

  // Effective schedule for the selected date (resolved by backend: holiday > weekly > legacy)
  const [effectiveSchedule, setEffectiveSchedule] = useState(null);
  const selectedDateStr = eventData.date ? format(eventData.date, "yyyy-MM-dd") : null;

  useEffect(() => {
    if (!selectedSede || !selectedDateStr) { setEffectiveSchedule(null); return; }
    API.get(`/sedes/${selectedSede.id}/schedule?date=${selectedDateStr}`)
      .then(r => setEffectiveSchedule(r.data))
      .catch(() => setEffectiveSchedule(null));
  }, [selectedSede, selectedDateStr]);

  const timeSlots = useMemo(() => {
    if (!selectedSede) return [];
    // Fallback chain: live effective schedule (if loaded) -> legacy fields
    const open = effectiveSchedule?.open || selectedSede.opening_time;
    const close = effectiveSchedule?.close || selectedSede.closing_time;
    const interval = effectiveSchedule?.time_interval || selectedSede.time_interval || 30;
    const closed = !!effectiveSchedule?.closed;
    if (closed || !open || !close) return [];
    const [oh, om] = open.split(":").map(Number);
    const [ch, cm] = close.split(":").map(Number);
    const startMin = oh * 60 + om;
    let endMin = ch * 60 + cm;
    if (endMin <= startMin) endMin += 24 * 60; // wraps past midnight
    const slots = [];
    for (let cur = startMin; cur < endMin; cur += interval) {
      const display = cur % (24 * 60);
      const h = Math.floor(display / 60);
      const m = display % 60;
      const suffix = h >= 12 ? "PM" : "AM";
      const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
      slots.push({ label: `${h12}:${String(m).padStart(2, "0")} ${suffix}`, minutes: display });
    }
    return slots;
  }, [selectedSede, effectiveSchedule]);

  const blocksForSelectedDate = useMemo(() => {
    if (!selectedDateStr) return [];
    return blockedDates.filter(b => b.date === selectedDateStr);
  }, [blockedDates, selectedDateStr]);

  const parseTimeToMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + (m || 0);
  };

  const availableTimeSlots = useMemo(() => {
    if (!selectedDateStr) return timeSlots.map(s => s.label);
    // Ventana m\u00ednima de anticipaci\u00f3n: bloquea horarios demasiado pr\u00f3ximos a la hora actual
    const leadHours = parseInt(landingConfig.booking_lead_hours) || 0;
    const leadCutoffMin = leadHours > 0
      ? (() => {
          const now = new Date();
          const todayStr = format(now, "yyyy-MM-dd");
          if (selectedDateStr !== todayStr) return -1; // solo aplica al d\u00eda de hoy
          return now.getHours() * 60 + now.getMinutes() + leadHours * 60;
        })()
      : -1;
    return timeSlots.filter(slot => {
      if (leadCutoffMin >= 0 && slot.minutes < leadCutoffMin) return false;
      for (const b of blocksForSelectedDate) {
        if (!b.start_time && !b.end_time) return false; // full-day block removes all
        if (b.start_time && b.end_time) {
          const s = parseTimeToMinutes(b.start_time);
          const e = parseTimeToMinutes(b.end_time);
          if (slot.minutes >= s && slot.minutes < e) return false;
        }
      }
      return true;
    }).map(s => s.label);
  }, [timeSlots, blocksForSelectedDate, selectedDateStr, landingConfig.booking_lead_hours]);

  // Clear selected time when it becomes unavailable (e.g. after date change into a blocked slot)
  useEffect(() => {
    if (eventData.time && !availableTimeSlots.includes(eventData.time)) {
      setEventData(p => ({ ...p, time: "" }));
    }
  }, [availableTimeSlots, eventData.time]);

  const disabledDates = useMemo(() => {
    // A date is fully blocked only when there is at least one full-day block.
    return blockedDates
      .filter(b => !b.start_time && !b.end_time)
      .map(b => new Date(b.date + "T00:00:00"));
  }, [blockedDates]);

  const filteredEventTypes = useMemo(() => {
    if (!selectedSede || !selectedSede.reservation_purpose_ids?.length) return reservationPurposes.filter(e => e.active);
    return reservationPurposes.filter(e => e.active && selectedSede.reservation_purpose_ids.includes(e.id));
  }, [selectedSede, reservationPurposes]);

  const sedeZoneTypes = useMemo(() => {
    if (!selectedSede?.zone_types_enabled || !selectedSede?.assigned_zone_type_ids?.length) return [];
    return allZoneTypes.filter(tt => selectedSede.assigned_zone_type_ids.includes(tt.id));
  }, [selectedSede, allZoneTypes]);

  const hasZoneTypes = sedeZoneTypes.length > 0;
  const showConditions = selectedSede?.conditions_enabled !== false && selectedSede?.show_conditions !== false;

  // Group sedes by city.
  const sedesByCity = useMemo(() => {
    const map = new Map();
    for (const s of sedes) {
      const key = (s.city || "").trim() || "Sin ciudad";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return map;
  }, [sedes]);

  const availableCities = useMemo(
    () => Array.from(sedesByCity.keys()).sort((a, b) => a.localeCompare(b, "es")),
    [sedesByCity]
  );

  // Effective city: explicit user choice OR auto-pick when only one city is available.
  // Computed (no state mutation) so we avoid an extra render and a lint warning.
  const effectiveCity = useMemo(() => {
    if (selectedCity) return selectedCity;
    if (availableCities.length === 1) return availableCities[0];
    return null;
  }, [selectedCity, availableCities]);

  const sedesInSelectedCity = useMemo(() => {
    if (!effectiveCity) return [];
    return sedesByCity.get(effectiveCity) || [];
  }, [sedesByCity, effectiveCity]);

  // Dynamic step mapping. City step is skipped automatically when only one city has sedes.
  // Corporate requests skip zone selection and the normal "confirm" success screen,
  // ending instead on a review step with a "Contactar por WhatsApp" action (5 steps vs 6).
  const steps = useMemo(() => {
    const isCorp = formData.numPeople === "corporate";
    const s = [];
    if (availableCities.length > 1) s.push("city");
    s.push("sede", "info", "purpose-date");
    if (showConditions) s.push("conditions");
    if (isCorp) {
      s.push("corporate-confirm");
    } else {
      if (hasZoneTypes) s.push("zones");
      s.push("confirm");
    }
    return s;
  }, [showConditions, hasZoneTypes, availableCities.length, formData.numPeople]);

  const totalSteps = steps.length;
  const currentStepType = steps[step] || "sede";

  const goNext = () => setStep(s => s + 1);
  const goBack = () => {
    if (step === 0) navigate("/");
    else setStep(s => s - 1);
  };

  const validateStep2 = () => {
    const { fullName, email, phone, numPeople } = formData;
    if (!fullName.trim()) { toast.error("Nombre completo requerido"); return false; }
    if (!email.trim() || !email.includes("@")) { toast.error("Email valido requerido"); return false; }
    if (!phone.trim()) { toast.error("Tel\u00e9fono requerido"); return false; }
    if (!numPeople) { toast.error("N\u00famero de personas requerido"); return false; }
    return true;
  };

  const corporateThreshold = landingConfig.corporate_threshold || 0;
  const isCorporateRequest = formData.numPeople === "corporate";

  const handleCorporateRedirect = () => {
    if (!landingConfig.whatsapp_link) {
      toast.error("WhatsApp no configurado. Contacta al administrador.");
      return;
    }
    const parts = [];
    parts.push("Hola, deseo realizar una reserva corporativa:");
    parts.push(`Nombre: ${formData.fullName}`);
    parts.push(`Correo: ${formData.email}`);
    parts.push(`Tel\u00e9fono: ${formData.countryCode} ${formData.phone}`);
    parts.push(`Personas: M\u00e1s de ${corporateThreshold}`);
    if (selectedSede?.name) parts.push(`Sede: ${selectedSede.name}`);
    if (eventData.reservationPurpose) parts.push(`Motivo: ${eventData.reservationPurpose}`);
    if (eventData.date) parts.push(`Fecha: ${format(eventData.date, "yyyy-MM-dd")}`);
    if (eventData.time) parts.push(`Hora: ${eventData.time}`);
    if (eventData.comments) parts.push(`Comentarios: ${eventData.comments}`);
    if (showConditions && conditions.length) parts.push("He le\u00eddo y acepto las condiciones de reserva.");
    const message = parts.join("\n");
    const base = landingConfig.whatsapp_link.trim();
    const separator = base.includes("?") ? "&" : "?";
    const url = `${base}${separator}text=${encodeURIComponent(message)}`;
    toast.success("Ser\u00e1s redirigido a WhatsApp para completar tu reserva corporativa");
    setTimeout(() => {
      if (isEmbed) {
        // When embedded inside an iframe (e.g. widget.js), the host browser may block
        // cross-origin navigation from a sandboxed frame. Delegate the redirect to the parent.
        try {
          window.parent.postMessage({ type: 'reserva-widget-open-url', url }, parentOrigin || '*');
        } catch (e) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }, 400);
  };

  const handleStep2Continue = () => {
    if (!validateStep2()) return;
    goNext();
  };

  const validateStep3 = () => {
    if (!eventData.reservationPurpose) { toast.error("Selecciona un motivo de reserva"); return false; }
    if (!eventData.date) { toast.error("Selecciona una fecha"); return false; }
    if (!eventData.time) { toast.error("Selecciona una hora"); return false; }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    let utm = {};
    try { utm = JSON.parse(sessionStorage.getItem("utm_params") || "{}"); } catch { utm = {}; }
    try {
      const { data } = await API.post("/reservations", {
        sede_id: selectedSede.id,
        full_name: formData.fullName,
        email: formData.email,
        phone: `${formData.countryCode} ${formData.phone}`,
        num_people: parseInt(formData.numPeople),
        reservation_purpose: eventData.reservationPurpose,
        date: format(eventData.date, "yyyy-MM-dd"),
        time: eventData.time,
        zone_type: selectedZone || null,
        comments: eventData.comments,
        reservation_type: "free",
        utm_source: utm.utm_source || null,
        utm_medium: utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        utm_content: utm.utm_content || null,
      });
      setReservation(data);
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear reserva");
    } finally {
      setSubmitting(false);
    }
  };

  const countryCodes = [
    { value: "+57", flag: "\u{1F1E8}\u{1F1F4}", name: "Colombia" },
    { value: "+1", flag: "\u{1F1FA}\u{1F1F8}", name: "Estados Unidos" },
    { value: "+52", flag: "\u{1F1F2}\u{1F1FD}", name: "Mexico" },
    { value: "+54", flag: "\u{1F1E6}\u{1F1F7}", name: "Argentina" },
    { value: "+55", flag: "\u{1F1E7}\u{1F1F7}", name: "Brasil" },
    { value: "+56", flag: "\u{1F1E8}\u{1F1F1}", name: "Chile" },
    { value: "+51", flag: "\u{1F1F5}\u{1F1EA}", name: "Peru" },
    { value: "+58", flag: "\u{1F1FB}\u{1F1EA}", name: "Venezuela" },
    { value: "+593", flag: "\u{1F1EA}\u{1F1E8}", name: "Ecuador" },
    { value: "+591", flag: "\u{1F1E7}\u{1F1F4}", name: "Bolivia" },
    { value: "+595", flag: "\u{1F1F5}\u{1F1FE}", name: "Paraguay" },
    { value: "+598", flag: "\u{1F1FA}\u{1F1FE}", name: "Uruguay" },
    { value: "+507", flag: "\u{1F1F5}\u{1F1E6}", name: "Panama" },
    { value: "+506", flag: "\u{1F1E8}\u{1F1F7}", name: "Costa Rica" },
    { value: "+502", flag: "\u{1F1EC}\u{1F1F9}", name: "Guatemala" },
    { value: "+503", flag: "\u{1F1F8}\u{1F1FB}", name: "El Salvador" },
    { value: "+504", flag: "\u{1F1ED}\u{1F1F3}", name: "Honduras" },
    { value: "+505", flag: "\u{1F1F3}\u{1F1EE}", name: "Nicaragua" },
    { value: "+53", flag: "\u{1F1E8}\u{1F1FA}", name: "Cuba" },
    { value: "+1809", flag: "\u{1F1E9}\u{1F1F4}", name: "Rep. Dominicana" },
    { value: "+34", flag: "\u{1F1EA}\u{1F1F8}", name: "Espana" },
    { value: "+44", flag: "\u{1F1EC}\u{1F1E7}", name: "Reino Unido" },
    { value: "+33", flag: "\u{1F1EB}\u{1F1F7}", name: "Francia" },
    { value: "+49", flag: "\u{1F1E9}\u{1F1EA}", name: "Alemania" },
    { value: "+39", flag: "\u{1F1EE}\u{1F1F9}", name: "Italia" },
    { value: "+351", flag: "\u{1F1F5}\u{1F1F9}", name: "Portugal" },
    { value: "+81", flag: "\u{1F1EF}\u{1F1F5}", name: "Japon" },
    { value: "+86", flag: "\u{1F1E8}\u{1F1F3}", name: "China" },
    { value: "+91", flag: "\u{1F1EE}\u{1F1F3}", name: "India" },
    { value: "+61", flag: "\u{1F1E6}\u{1F1FA}", name: "Australia" },
    { value: "+82", flag: "\u{1F1F0}\u{1F1F7}", name: "Corea del Sur" },
    { value: "+7", flag: "\u{1F1F7}\u{1F1FA}", name: "Rusia" },
    { value: "+27", flag: "\u{1F1FF}\u{1F1E6}", name: "Sudafrica" },
    { value: "+234", flag: "\u{1F1F3}\u{1F1EC}", name: "Nigeria" },
    { value: "+20", flag: "\u{1F1EA}\u{1F1EC}", name: "Egipto" },
    { value: "+971", flag: "\u{1F1E6}\u{1F1EA}", name: "Emiratos Arabes" },
    { value: "+966", flag: "\u{1F1F8}\u{1F1E6}", name: "Arabia Saudita" },
    { value: "+90", flag: "\u{1F1F9}\u{1F1F7}", name: "Turquia" },
    { value: "+48", flag: "\u{1F1F5}\u{1F1F1}", name: "Polonia" },
    { value: "+31", flag: "\u{1F1F3}\u{1F1F1}", name: "Paises Bajos" },
    { value: "+41", flag: "\u{1F1E8}\u{1F1ED}", name: "Suiza" },
    { value: "+46", flag: "\u{1F1F8}\u{1F1EA}", name: "Suecia" },
    { value: "+47", flag: "\u{1F1F3}\u{1F1F4}", name: "Noruega" },
    { value: "+45", flag: "\u{1F1E9}\u{1F1F0}", name: "Dinamarca" },
    { value: "+358", flag: "\u{1F1EB}\u{1F1EE}", name: "Finlandia" },
    { value: "+353", flag: "\u{1F1EE}\u{1F1EA}", name: "Irlanda" },
    { value: "+30", flag: "\u{1F1EC}\u{1F1F7}", name: "Grecia" },
    { value: "+972", flag: "\u{1F1EE}\u{1F1F1}", name: "Israel" },
    { value: "+65", flag: "\u{1F1F8}\u{1F1EC}", name: "Singapur" },
    { value: "+66", flag: "\u{1F1F9}\u{1F1ED}", name: "Tailandia" },
    { value: "+60", flag: "\u{1F1F2}\u{1F1FE}", name: "Malasia" },
    { value: "+63", flag: "\u{1F1F5}\u{1F1ED}", name: "Filipinas" },
    { value: "+62", flag: "\u{1F1EE}\u{1F1E9}", name: "Indonesia" },
    { value: "+84", flag: "\u{1F1FB}\u{1F1F3}", name: "Vietnam" },
    { value: "+64", flag: "\u{1F1F3}\u{1F1FF}", name: "Nueva Zelanda" },
  ];

  const selectedCountry = countryCodes.find(c => c.value === formData.countryCode) || countryCodes[0];

  const renderStep = () => {
    // Step: Select city (only shown when multiple cities have sedes)
    if (currentStepType === "city") return (
      <div className="animate-slide-in" data-testid="step-select-city">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-1">Selecciona tu ciudad</h2>
          <p className="text-sm text-muted-foreground">Elige la ciudad donde quieres reservar</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {availableCities.map(city => {
            const count = sedesByCity.get(city)?.length || 0;
            const isSelected = effectiveCity === city;
            return (
              <button
                key={city}
                type="button"
                data-testid={`city-card-${city}`}
                onClick={() => { setSelectedCity(city); setSelectedSede(null); }}
                className={`flex items-center justify-between gap-2 p-3 rounded-xl border text-left transition-colors ${isSelected ? 'border-[#c9a84c] bg-[#c9a84c]/10' : 'border-border bg-card hover:bg-secondary/40'}`}
              >
                <div className="min-w-0 flex items-center gap-2">
                  <MapPin className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-[#c9a84c]' : 'text-muted-foreground'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{city}</p>
                    <p className="text-xs text-muted-foreground">{count} {count === 1 ? 'sede' : 'sedes'}</p>
                  </div>
                </div>
                {isSelected && <Check className="w-4 h-4 text-[#c9a84c] flex-shrink-0" />}
              </button>
            );
          })}
        </div>
        <div className="mt-6">
          <Button data-testid="city-next-btn" className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold" disabled={!effectiveCity} onClick={goNext}>
            Continuar <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );

    // Step 0: Select Sede (filtered by selected city)
    if (currentStepType === "sede") return (
      <div className="animate-slide-in" data-testid="step-select-sede">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-1">Selecciona una sede</h2>
          <p className="text-sm text-muted-foreground">
            {effectiveCity && availableCities.length > 1
              ? <>Sedes disponibles en <span className="text-[#c9a84c] font-medium">{effectiveCity}</span></>
              : "Elige la ubicaci\u00f3n que prefieras"}
          </p>
        </div>
        <div className="space-y-3">
          {(effectiveCity ? sedesInSelectedCity : sedes).map(sede => (
            <div
              key={sede.id}
              data-testid={`sede-card-${sede.id}`}
              className={`sede-card flex items-center gap-3 bg-card border border-border rounded-xl p-3 ${selectedSede?.id === sede.id ? 'selected' : ''}`}
              onClick={() => setSelectedSede(sede)}
            >
              {sede.image_url ? (
                <img
                  src={sede.image_url}
                  alt={sede.name}
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  onError={e => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                data-testid={`sede-icon-fallback-${sede.id}`}
                className="w-14 h-14 rounded-lg flex-shrink-0 bg-[#252542] items-center justify-center text-[#c9a84c]"
                style={{ display: sede.image_url ? 'none' : 'flex' }}
              >
                <Store className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm">{sede.name}</h3>
                <p className="text-xs text-muted-foreground truncate">{sede.description}</p>
                <div className="flex items-center gap-1 mt-1 text-xs text-[#c9a84c]">
                  <MapPin className="w-3 h-3" /> {sede.address}
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedSede?.id === sede.id ? 'bg-[#c9a84c] border-[#c9a84c]' : 'border-border'}`}>
                {selectedSede?.id === sede.id && <Check className="w-3 h-3 text-[#0f0f1a]" />}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <Button data-testid="step0-next-btn" className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold" disabled={!selectedSede} onClick={goNext}>
            Continuar <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );

    // Step 1: User Info
    if (currentStepType === "info") return (
      <div className="animate-slide-in" data-testid="step-user-info">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-1 text-[#f0ece2]">Tu informaci&oacute;n</h2>
          <p className="text-sm text-[#a0a0b8]">Datos de contacto para tu reserva</p>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#a0a0b8]">Nombre completo</Label>
            <Input data-testid="fullname-input" placeholder="Ej: Juan Perez" value={formData.fullName}
              onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))} className="mt-1 bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2] placeholder:text-[#555] focus:border-[#c9a84c] focus:ring-[rgba(201,168,76,0.15)]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#a0a0b8]">Correo electr&oacute;nico</Label>
            <Input data-testid="email-input" type="email" placeholder="correo@ejemplo.com" value={formData.email}
              onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2] placeholder:text-[#555] focus:border-[#c9a84c]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#a0a0b8]">Tel&eacute;fono</Label>
            <div className="flex gap-2 mt-1">
              <Select value={formData.countryCode} onValueChange={v => setFormData(p => ({ ...p, countryCode: v }))}>
                <SelectTrigger data-testid="country-code-select" className="w-[120px] bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2]">
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="text-base leading-none">{selectedCountry.flag}</span>
                    <span className="text-xs">{selectedCountry.value}</span>
                  </span>
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-[rgba(201,168,76,0.2)] text-[#f0ece2] max-h-64">
                  {countryCodes.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2">
                        <span className="text-base leading-none">{c.flag}</span>
                        <span className="text-xs font-medium">{c.value}</span>
                        <span className="text-xs text-[#a0a0b8]">{c.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input data-testid="phone-input" type="tel" placeholder="300 000 0000" value={formData.phone}
                onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} className="flex-1 bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2] placeholder:text-[#555] focus:border-[#c9a84c]" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#a0a0b8]">N&uacute;mero de personas</Label>
            <Select value={formData.numPeople} onValueChange={v => setFormData(p => ({ ...p, numPeople: v }))}>
              <SelectTrigger data-testid="num-people-select" className="mt-1 bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2]"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-[rgba(201,168,76,0.2)] text-[#f0ece2]">
                {Array.from({ length: selectedSede?.max_people || 10 }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{i + 1} {i === 0 ? 'persona' : 'personas'}</SelectItem>
                ))}
                {corporateThreshold > 0 && landingConfig.whatsapp_link && (
                  <SelectItem key="corporate" value="corporate" data-testid="num-people-corporate-option">
                    M&aacute;s de {corporateThreshold} personas
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {isCorporateRequest && (
              <p className="text-xs text-[#c9a84c] mt-2" data-testid="corporate-hint">
                Es una reserva corporativa. Contin&uacute;a para indicar motivo, fecha y condiciones; al final podr&aacute;s contactarnos por WhatsApp con todos tus datos.
              </p>
            )}
          </div>
        </div>
        <div className="mt-6 space-y-2">
          <Button data-testid="step1-next-btn" className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold"
            onClick={handleStep2Continue}>
            Continuar <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );

    // Step 2: Event & Date
    if (currentStepType === "purpose-date") return (
      <div className="animate-slide-in" data-testid="step-event-date">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-1 text-[#f0ece2]">Motivo y fecha</h2>
          <p className="text-sm text-[#a0a0b8]">Selecciona el motivo de reserva y horario</p>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#a0a0b8] mb-2 block">Motivo de reserva</Label>
            <div className="grid grid-cols-2 gap-2">
              {filteredEventTypes.map(et => {
                const IconComp = ICON_MAP[et.icon] || Star;
                return (
                  <div key={et.id} data-testid={`event-type-${et.id}`}
                    className={`event-card flex flex-col items-center justify-center gap-1 p-3 bg-[#252542] border rounded-lg ${eventData.reservationPurpose === et.name ? 'selected border-[#c9a84c]' : 'border-[rgba(201,168,76,0.2)]'}`}
                    onClick={() => setEventData(p => ({ ...p, reservationPurpose: et.name }))}>
                    <IconComp className={`w-5 h-5 ${eventData.reservationPurpose === et.name ? 'text-[#c9a84c]' : 'text-[#a0a0b8]'}`} />
                    <span className={`text-xs font-medium ${eventData.reservationPurpose === et.name ? 'text-[#c9a84c]' : 'text-[#a0a0b8]'}`}>{et.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#a0a0b8] mb-1 block">Fecha</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button data-testid="date-picker-btn" variant="outline" className="w-full justify-start bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2] text-left font-normal hover:bg-[#1a1a2e]">
                    <CalendarIcon className="w-4 h-4 mr-2 text-[#c9a84c]" />
                    {eventData.date ? format(eventData.date, "dd/MM/yyyy") : "Selecciona"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1a1a2e] border-[rgba(201,168,76,0.2)]" align="start">
                  <Calendar
                    mode="single"
                    selected={eventData.date}
                    onSelect={d => {
                      setEventData(p => ({ ...p, date: d }));
                      setCalendarOpen(false);
                    }}
                    disabled={[{ before: new Date() }, ...disabledDates]}
                    modifiers={{ blocked: disabledDates }}
                    modifiersClassNames={{ blocked: "rdp-day-blocked" }}
                    locale={es}
                    initialFocus
                    classNames={{
                      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                      month: "space-y-4",
                      caption: "flex justify-center pt-1 relative items-center",
                      caption_label: "text-sm font-semibold text-[#f0ece2]",
                      nav: "space-x-1 flex items-center",
                      nav_button: "h-7 w-7 bg-[#252542] p-0 text-[#c9a84c] hover:text-[#f0ece2] hover:bg-[#c9a84c]/20 rounded-md border border-[rgba(201,168,76,0.3)] inline-flex items-center justify-center transition-colors",
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex",
                      head_cell: "text-[#c9a84c] rounded-md w-8 font-semibold text-[0.75rem] uppercase",
                      row: "flex w-full mt-2",
                      cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#c9a84c]/20 [&:has([aria-selected])]:rounded-md",
                      day: "h-8 w-8 p-0 font-normal text-[#e8e4da] hover:bg-[#c9a84c]/15 hover:text-[#f0ece2] rounded-md inline-flex items-center justify-center cursor-pointer transition-colors aria-selected:opacity-100",
                      day_range_start: "day-range-start",
                      day_range_end: "day-range-end",
                      day_selected: "bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] hover:text-[#0f0f1a] focus:bg-[#c9a84c] focus:text-[#0f0f1a] font-bold",
                      day_today: "bg-[#252542] text-[#c9a84c] font-bold ring-1 ring-[#c9a84c]/60",
                      day_outside: "text-[#555] opacity-30 hover:bg-transparent",
                      day_disabled: "text-[#666] opacity-45 cursor-not-allowed hover:bg-transparent",
                      day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                      day_hidden: "invisible",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#a0a0b8] mb-1 block">Hora</Label>
              <Select value={eventData.time} onValueChange={v => setEventData(p => ({ ...p, time: v }))}>
                <SelectTrigger data-testid="time-select" className="bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2]"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-[rgba(201,168,76,0.2)] text-[#f0ece2] max-h-60">
                  {availableTimeSlots.length === 0 && (
                    <div className="px-3 py-2 text-xs text-[#a0a0b8]">No hay horarios disponibles en esta fecha</div>
                  )}
                  {availableTimeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#a0a0b8]">Comentarios adicionales</Label>
            <Textarea data-testid="comments-input" placeholder="Alergias, preferencias, peticiones especiales..."
              value={eventData.comments} onChange={e => setEventData(p => ({ ...p, comments: e.target.value }))}
              className="mt-1 bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2] placeholder:text-[#555] min-h-[60px] focus:border-[#c9a84c]" />
          </div>
        </div>
        <div className="mt-6">
          <Button data-testid="step2-next-btn" className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold"
            onClick={() => validateStep3() && goNext()}>Continuar <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </div>
    );

    // Step 3: Conditions
    if (currentStepType === "conditions") return (
      <div className="animate-slide-in" data-testid="step-conditions">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-1">Condiciones de reserva</h2>
          <p className="text-sm text-muted-foreground">Por favor revisa nuestras pol&iacute;ticas</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 space-y-0">
          {conditions.map((c, i) => {
            const IconComp = ICON_MAP[c.icon] || Star;
            return (
              <div key={c.id} className={`flex gap-3 py-3 ${i < conditions.length - 1 ? 'border-b border-border/50' : ''}`}>
                <div className="w-8 h-8 rounded-lg bg-[#c9a84c]/10 flex items-center justify-center flex-shrink-0">
                  <IconComp className="w-4 h-4 text-[#c9a84c]" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold font-heading">{c.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{c.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6">
          <Button data-testid="accept-conditions-btn" className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold" onClick={steps[step + 1] === "confirm" ? handleSubmit : goNext}>
            {steps[step + 1] === "confirm" ? 'Confirmar reserva' : 'Acepto, continuar'} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );

    // Step 4: Table Type (only if hasZoneTypes)
    if (currentStepType === "zones") return (
      <div className="animate-slide-in" data-testid="step-zone-type">
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-0.5">Selecciona tu zona</h2>
          <p className="text-xs text-muted-foreground">Elige el tipo de zona que prefieras</p>
        </div>
        <div className="space-y-2">
          {sedeZoneTypes.map(zt => (
            <div key={zt.id} data-testid={`zone-type-${zt.id}`}
              className={`table-card flex items-center gap-3 bg-card border border-border rounded-lg p-2 cursor-pointer overflow-hidden ${selectedZone === zt.name ? 'selected' : ''}`}
              onClick={() => setSelectedZone(selectedZone === zt.name ? null : zt.name)}>
              {zt.image_url ? (
                <img src={zt.image_url} alt={zt.name} className="w-14 h-14 rounded-md object-cover flex-shrink-0"
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-14 h-14 rounded-md bg-[#252542] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-sm font-heading truncate">{zt.name}</h3>
                  <span className="text-[10px] text-[#c9a84c] bg-[#c9a84c]/10 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                    Hasta {zt.max_seats}
                  </span>
                </div>
                {zt.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{zt.description}</p>}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          <Button data-testid="step4-next-btn" className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold" onClick={handleSubmit}>
            Confirmar reserva <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button variant="ghost" className="w-full text-muted-foreground text-xs h-8" onClick={handleSubmit}>
            Omitir selecci&oacute;n de zona
          </Button>
        </div>
      </div>
    );

    // Step: Corporate review + WhatsApp contact (replaces zones + confirm for corporate requests)
    if (currentStepType === "corporate-confirm") return (
      <div className="animate-slide-in" data-testid="step-corporate-confirm">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-1">Reserva corporativa</h2>
          <p className="text-sm text-muted-foreground">Revisa tus datos y cont&aacute;ctanos por WhatsApp para coordinar tu reserva</p>
        </div>
        <div className="bg-card border border-border rounded-xl px-3 py-1.5 text-left">
          {[
            ["Sede", selectedSede?.name],
            ["Nombre", formData.fullName],
            ["Correo", formData.email],
            ["Tel\u00e9fono", `${formData.countryCode} ${formData.phone}`],
            ["Personas", `M\u00e1s de ${corporateThreshold}`],
            ["Motivo", eventData.reservationPurpose],
            eventData.date ? ["Fecha", format(eventData.date, "dd/MM/yyyy")] : null,
            eventData.time ? ["Hora", eventData.time] : null,
            eventData.comments ? ["Comentarios", eventData.comments] : null,
          ].filter(Boolean).filter(([, v]) => v).map(([label, val]) => (
            <div key={label} className="flex justify-between gap-3 py-1.5 border-b border-border/30 last:border-0 text-xs">
              <span className="text-muted-foreground flex-shrink-0">{label}</span>
              <span className="font-medium text-right break-words">{val}</span>
            </div>
          ))}
        </div>
        {showConditions && conditions.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3" data-testid="corporate-conditions-note">
            Al contactarnos confirmas que has le&iacute;do y aceptas las condiciones de reserva.
          </p>
        )}
        <div className="mt-6">
          <Button data-testid="corporate-whatsapp-btn" className="w-full bg-[#25D366] text-white hover:bg-[#1da851] font-semibold"
            onClick={handleCorporateRedirect}>
            <MessageCircle className="w-4 h-4 mr-2" /> Contactar por WhatsApp
          </Button>
        </div>
      </div>
    );

    // Step: Confirmation
    if (currentStepType === "confirm" && reservation) return (
      <div className="animate-slide-in text-center" data-testid="step-confirmation">
        <div className="animate-scale-in w-11 h-11 rounded-full bg-green-900/20 border-2 border-green-500 flex items-center justify-center mx-auto mb-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2.5">
            <path d="M5 13l4 4L19 7" strokeDasharray="60" strokeDashoffset="60" style={{ animation: 'drawCheck 0.6s ease 0.3s forwards' }} />
          </svg>
        </div>
        <h2 className="text-lg font-bold mb-0.5">Reserva Confirmada</h2>
        <p className="text-xs text-muted-foreground mb-3">Tu reserva ha sido registrada exitosamente</p>
        <div className="bg-gradient-to-br from-[#c9a84c]/15 to-[#c9a84c]/5 border border-dashed border-[#c9a84c] rounded-lg px-3 py-2 mb-3 inline-block">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">C&oacute;digo de reserva</div>
          <div className="text-lg font-bold text-[#c9a84c] tracking-widest" data-testid="reservation-code">{reservation.reservation_code}</div>
        </div>
        <div className="bg-card rounded-lg px-3 py-1.5 text-left max-w-sm mx-auto">
          {[
            ["Sede", reservation.sede_name],
            ["Nombre", reservation.full_name],
            ["Fecha", reservation.date],
            ["Hora", reservation.time],
            ["Personas", reservation.num_people],
            ["Motivo", reservation.reservation_purpose],
            ...(reservation.zone_type ? [["Zona", reservation.zone_type]] : []),
          ].map(([label, val]) => (
            <div key={label} className="flex justify-between py-1 border-b border-border/30 last:border-0 text-xs">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-medium">{val}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-1.5">
          <Button data-testid="new-reservation-btn" size="sm" className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold h-9"
            onClick={() => { setStep(0); setSelectedSede(null); setSelectedCity(null); setFormData({ fullName: "", email: "", phone: "", countryCode: "+57", numPeople: "" }); setEventData({ reservationPurpose: "", date: null, time: "", comments: "" }); setSelectedZone(null); setReservation(null); }}>
            Hacer otra reserva
          </Button>
          {!isEmbed && (
            <Button data-testid="go-to-reservations-btn" size="sm" variant="outline" className="w-full border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10 h-8 text-xs"
              onClick={() => navigate("/acceder")}>
              Ver mis reservas
            </Button>
          )}
        </div>
      </div>
    );

    return null;
  };

  return (
    <div className="dark min-h-screen flex flex-col items-center px-4 py-4" style={{background:'#0f0f1a',color:'#f0ece2'}} data-testid="reservation-wizard">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-2">
          {!isEmbed ? (
            <Button data-testid="wizard-back-btn" variant="ghost" size="sm" className="text-[#a0a0b8] hover:text-[#f0ece2] hover:bg-[#c9a84c]/15" onClick={goBack}>
              <ArrowLeft className="w-4 h-4 mr-1" /> {step === 0 ? 'Inicio' : 'Atr\u00e1s'}
            </Button>
          ) : step > 0 ? (
            <Button data-testid="wizard-back-btn" variant="ghost" size="sm" className="text-[#a0a0b8] hover:text-[#f0ece2] hover:bg-[#c9a84c]/15" onClick={() => setStep(s => Math.max(0, s - 1))}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Atr&aacute;s
            </Button>
          ) : <div />}
          <span className="text-xs text-[#a0a0b8]">Paso {step + 1} de {totalSteps}</span>
        </div>
        {currentStepType !== "confirm" && (
          <ProgressBar current={step} total={totalSteps} />
        )}
        <div className="mt-4">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
